/**
 * ==============================================================================
 * MITRE ATT&CK MATRIX COMPONENT
 * ==============================================================================
 * Interactive visualization of the MITRE ATT&CK framework coverage matrix
 * Displays techniques organized by tactics with color-coded coverage levels
 * Version: 1.0 | Date: 2025-07-27
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * ATTACKMatrix Component
 * Renders the MITRE ATT&CK matrix as an interactive table
 * 
 * @param {Array} techniques - Array of technique objects with coverage data
 * @param {Array} tactics - Array of tactic objects in display order
 * @param {Object} techniquesByTactic - Mapping of tactic IDs to technique IDs
 * @param {Function} onTechniqueClick - Callback when a technique is clicked
 */
const ATTACKMatrix = ({ techniques, tactics, techniquesByTactic, onTechniqueClick }) => {
    
    /**
     * Create a mapping of technique IDs to technique objects for quick lookup
     */
    const techniqueMap = useMemo(() => {
        const map = {};
        techniques.forEach(technique => {
            map[technique.id] = technique;
        });
        return map;
    }, [techniques]);

    /**
     * Get the appropriate Tailwind CSS class based on coverage level
     * @param {Object} technique - The technique object
     * @returns {string} - Tailwind CSS classes for styling
     */
    const getCellStyle = (technique) => {
        if (!technique) return '';

        const baseClasses = 'p-2 text-xs rounded cursor-pointer transition-all duration-200 hover:shadow-lg transform hover:scale-105';
        
        switch (technique.coverageLevel) {
            case 'excellent':
                return `${baseClasses} bg-green-700 text-white hover:bg-green-800`;
            case 'good':
                return `${baseClasses} bg-green-500 text-white hover:bg-green-600`;
            case 'partial':
                return `${baseClasses} bg-yellow-500 text-gray-900 hover:bg-yellow-600`;
            case 'none':
                return `${baseClasses} bg-red-700 text-white hover:bg-red-800`;
            default:
                return `${baseClasses} bg-gray-200 text-gray-600 hover:bg-gray-300`;
        }
    };

    /**
     * Get risk indicator icon based on risk level
     * @param {string} riskLevel - The risk level of the technique
     * @returns {string} - Emoji indicator
     */
    const getRiskIndicator = (riskLevel) => {
        switch (riskLevel) {
            case 'critical':
                return '🔴';
            case 'high':
                return '🟠';
            case 'medium':
                return '🟡';
            case 'low':
                return '🟢';
            default:
                return '';
        }
    };

    /**
     * Render a technique cell
     * @param {Object} technique - The technique object
     * @returns {JSX.Element} - The rendered cell
     */
    const renderTechniqueCell = (technique) => {
        if (!technique) {
            return <div className="p-2"></div>;
        }

        return (
            <button
                onClick={() => onTechniqueClick(technique.id)}
                className={getCellStyle(technique)}
                title={`${technique.name}\nControls: ${technique.totalControls}\nRisk: ${technique.riskLevel}`}
            >
                <div className="flex flex-col items-center justify-center h-full">
                    <span className="font-mono text-xs">{technique.id}</span>
                    <span className="text-xs mt-1">
                        {getRiskIndicator(technique.riskLevel)}
                    </span>
                    {technique.totalControls > 0 && (
                        <span className="text-xs mt-1 font-semibold">
                            {technique.passControls}/{technique.totalControls}
                        </span>
                    )}
                </div>
            </button>
        );
    };

    /**
     * Group techniques by their row for better visualization
     * Each row contains techniques that appear in the same horizontal line
     */
    const techniqueRows = useMemo(() => {
        const rows = [];
        const maxTechniquesPerRow = Math.max(...tactics.map(tactic => 
            (techniquesByTactic[tactic.id] || []).length
        ));

        for (let rowIndex = 0; rowIndex < maxTechniquesPerRow; rowIndex++) {
            const row = [];
            tactics.forEach(tactic => {
                const tacticTechniques = techniquesByTactic[tactic.id] || [];
                const techniqueId = tacticTechniques[rowIndex];
                row.push(techniqueId ? techniqueMap[techniqueId] : null);
            });
            rows.push(row);
        }

        return rows;
    }, [tactics, techniquesByTactic, techniqueMap]);

    return (
        <div className="overflow-x-auto" data-testid="attack-matrix">
            <table className="min-w-full border-collapse">
                {/* Table Header - Tactics */}
                <thead>
                    <tr>
                        {tactics.map(tactic => (
                            <th
                                key={tactic.id}
                                className="px-4 py-3 bg-gray-800 text-white text-sm font-medium text-center border border-gray-700"
                                style={{ minWidth: '150px' }}
                            >
                                <div className="flex flex-col items-center">
                                    <span className="font-semibold">{tactic.name}</span>
                                    <span className="text-xs text-gray-300 mt-1">
                                        {tactic.coveredTechniques}/{tactic.totalTechniques} cubiertas
                                    </span>
                                    <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                                        <div
                                            className="bg-green-500 h-2 rounded-full transition-all duration-500"
                                            style={{ width: `${tactic.coveragePercentage}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>

                {/* Table Body - Techniques */}
                <tbody>
                    {techniqueRows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {row.map((technique, colIndex) => (
                                <td
                                    key={`${rowIndex}-${colIndex}`}
                                    className="p-1 border border-gray-300 text-center align-middle"
                                    style={{ minHeight: '60px' }}
                                >
                                    {renderTechniqueCell(technique)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Summary Statistics */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="bg-gray-100 p-3 rounded">
                    <div className="font-semibold text-gray-700">Total Técnicas</div>
                    <div className="text-2xl font-bold text-gray-900">{techniques.length}</div>
                </div>
                <div className="bg-green-100 p-3 rounded">
                    <div className="font-semibold text-green-700">Cubiertas</div>
                    <div className="text-2xl font-bold text-green-900">
                        {techniques.filter(t => t.totalControls > 0).length}
                    </div>
                </div>
                <div className="bg-red-100 p-3 rounded">
                    <div className="font-semibold text-red-700">Gaps</div>
                    <div className="text-2xl font-bold text-red-900">
                        {techniques.filter(t => t.totalControls === 0).length}
                    </div>
                </div>
                <div className="bg-blue-100 p-3 rounded">
                    <div className="font-semibold text-blue-700">Riesgos Críticos</div>
                    <div className="text-2xl font-bold text-blue-900">
                        {techniques.filter(t => t.riskLevel === 'critical').length}
                    </div>
                </div>
            </div>
        </div>
    );
};

// PropTypes validation
ATTACKMatrix.propTypes = {
    techniques: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        totalControls: PropTypes.number.isRequired,
        passControls: PropTypes.number.isRequired,
        coverageLevel: PropTypes.oneOf(['none', 'partial', 'good', 'excellent']).isRequired,
        riskLevel: PropTypes.oneOf(['low', 'medium', 'high', 'critical']).isRequired
    })).isRequired,
    tactics: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        order: PropTypes.number.isRequired,
        totalTechniques: PropTypes.number.isRequired,
        coveredTechniques: PropTypes.number.isRequired,
        coveragePercentage: PropTypes.number.isRequired
    })).isRequired,
    techniquesByTactic: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
    onTechniqueClick: PropTypes.func.isRequired
};

export default ATTACKMatrix;